"use client";

import { useEffect, useState } from "react";

export type HydratedCommunicationBodyState = {
  /** Resolved full body (inline or S3), or null while loading / on empty. */
  body: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Client dual-read for communication bodies after UC-COMMS-BODY offload.
 * When `bodyS3Key` is set and inline `body` is empty, fetches
 * `GET /api/communications/[id]/body` (session cookies).
 */
export function useHydratedCommunicationBody(options: {
  communicationId: string | null | undefined;
  inlineBody?: string | null;
  bodyS3Key?: string | null;
  /** Fallback while loading / if hydrate fails (usually body_preview). */
  previewSnippet?: string | null;
}): HydratedCommunicationBodyState {
  const communicationId =
    typeof options.communicationId === "string" ? options.communicationId.trim() : "";
  const inline =
    typeof options.inlineBody === "string" ? options.inlineBody.trim() : "";
  const bodyS3Key =
    typeof options.bodyS3Key === "string" ? options.bodyS3Key.trim() : "";
  const preview =
    typeof options.previewSnippet === "string" ? options.previewSnippet.trim() : "";

  const needsS3Hydrate = Boolean(communicationId && bodyS3Key && !inline);

  const [fullBody, setFullBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(needsS3Hydrate);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!needsS3Hydrate) {
      setLoading(false);
      setFullBody(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const res = await fetch(`/api/communications/${communicationId}/body`, {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load full body (${res.status})`);
        }
        const data = (await res.json()) as { body?: string | null };
        if (!cancelled) {
          setFullBody(
            typeof data.body === "string" && data.body.trim()
              ? data.body.trim()
              : null
          );
        }
      } catch {
        if (!cancelled) {
          setError("Could not load the full message from storage.");
          setFullBody(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [communicationId, needsS3Hydrate]);

  const body = inline || fullBody || preview || null;
  return { body, loading, error };
}
